/**
 * Retro Theme Definition
 * Classic 8-bit pixel art aesthetic with green accent colors
 * Matches the original styling of 8-Bit Composer
 */

import type { Theme } from './types';

/**
 * Retro theme object
 * Preserves the beloved 8-bit aesthetic with Press Start 2P font,
 * green accent colors, and pixel-perfect spacing
 */
export const retroTheme: Theme = {
  name: 'retro',

  /**
   * Color palette
   * Classic terminal-inspired colors with bright green accents
   */
  colors: {
    // Background colors - deep black for authentic retro feel
    bg: '#080808',
    bgSecondary: '#111111',
    surface: '#222222',

    // Foreground colors - bright green for maximum contrast
    fg: '#0f0',
    fgSecondary: '#0c0',

    // Accent colors - various shades of green
    accent: '#0f0',
    accentHover: '#0f0',
    accentActive: '#0f0',

    // UI element colors
    border: '#555555',
    borderHover: '#0f0',
    cellBg: '#222222',
    cellBorder: '#555555',
    cellBorderActive: '#0f0',

    // Semantic colors - retro-styled status colors
    success: '#0f0',
    warning: '#ff0',
    error: '#f00',
    info: '#0ff',
  },

  /**
   * Typography
   * Press Start 2P font for authentic 8-bit text rendering
   */
  typography: {
    fontFamily: "'Press Start 2P', monospace",
    fontFamilyMono: "'Press Start 2P', monospace",
    fontSize: {
      xs: '0.6rem',
      sm: '0.7rem',
      base: '0.8rem',
      lg: '1rem',
      xl: '1.2rem',
      '2xl': '1.5rem',
    },
    fontWeight: {
      normal: 400,
      medium: 400,
      semibold: 400,
      bold: 700,
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.8,
    },
  },

  /**
   * Spacing
   * Pixel-perfect spacing values for precise layout control
   */
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
  },

  /**
   * Border radius
   * Minimal rounding for sharp, pixelated edges
   */
  borderRadius: {
    sm: '4px',
    md: '4px',
    lg: '6px',
    xl: '8px',
    full: '9999px',
  },

  /**
   * Shadows
   * Simple shadows with green glow effects
   */
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    md: '0 2px 8px rgba(0, 0, 0, 0.3)',
    lg: '0 4px 12px rgba(0, 0, 0, 0.4)',
    glow: '0 0 8px #0f0',
  },

  /**
   * Transitions
   * Quick transitions for snappy, responsive feel
   */
  transitions: {
    fast: '100ms ease',
    base: '200ms ease',
    slow: '300ms ease',
  },
};
